import React from 'react';
import { Link } from 'react-router-dom';
import { IonicButton, IonicInput } from 'ionic-ui';
import Layout from './Layout';
import PasswordInput from '../../components/ui/PasswordInput';
import GoogleAuthButton from '../../components/auth-components/GoogleAuthButton';
import { Controller } from 'react-hook-form';
import useSignIn from '../../services/authentication/hooks/useSignIn';
import useNativeGoogleAuth from '../../hooks/useNativeGoogleAuth';
import Divider from '../../components/ui/Divider';
import { phoneSchema } from '../../schemas/validation';
import { emailSchema } from '../../schemas/others';

const SignIn: React.FC = () => {
  const [loginMode, setLoginMode] = React.useState<'email' | 'phone'>('email');
  const touchStartX = React.useRef<number | null>(null);

  // SERVICE HOOK FOR LOGIN AUTHENTICATION
  const {
    formHandler: {
      control,
      register,
      trigger,
      getValues,
      formState: { errors },
    },
    handleFormSubmission,
  } = useSignIn();

  //GOOGLE AUTH FOR NATIVE APPS
  useNativeGoogleAuth();

  const onTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    touchStartX.current = event.changedTouches[0]?.clientX ?? null;
  };

  const changeMode = async (nextMode: 'email' | 'phone') => {
    if (nextMode === loginMode) return;

    if (loginMode === 'email') {
      const email = getValues('email');
      if (email && !emailSchema.safeParse(email).success) {
        await trigger('email');
      }
    }

    if (loginMode === 'phone') {
      const phone = getValues('phone');
      if (phone && !phoneSchema.safeParse(phone).success) {
        await trigger('phone');
      }
    }

    setLoginMode(nextMode);
  };

  const onTouchEnd = async (event: React.TouchEvent<HTMLDivElement>) => {
    if (touchStartX.current === null) return;

    const endX = event.changedTouches[0]?.clientX ?? touchStartX.current;
    const deltaX = endX - touchStartX.current;
    touchStartX.current = null;

    if (Math.abs(deltaX) < 40) return;

    const nextMode = deltaX < 0 ? 'phone' : 'email';
    await changeMode(nextMode);
  };

  return (
    <>
      <Layout
        uiType="signin"
        title="Welcome back"
        description="Welcome back! Please enter your details."
      >
        <form
          onSubmit={handleFormSubmission(loginMode)}
          className="flex flex-col gap-4 w-full py-4"
        >
          {/**Google signin */}
          <GoogleAuthButton />

          {/**Form divider */}
          <Divider text="OR" />

          <div
            className="rounded-md border border-border p-1.5 bg-surface-accent select-none"
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
          >
            <div className="relative flex items-center rounded-md overflow-hidden">
              <div
                className={`absolute inset-y-0 left-0 w-1/2 rounded-md bg-primary text-white shadow-sm transition-transform duration-300 ${
                  loginMode === 'phone' ? 'translate-x-full' : 'translate-x-0'
                }`}
              />
              <button
                type="button"
                className={`relative z-10 w-1/2 rounded-md py-2 text-sm font-semibold transition-colors ${
                  loginMode === 'email' ? 'text-white' : 'text-text-dark-medium'
                }`}
                onClick={() => {
                  void changeMode('email');
                }}
              >
                Email Login
              </button>
              <button
                type="button"
                className={`relative z-10 w-1/2 rounded-md py-2 text-sm font-semibold transition-colors ${
                  loginMode === 'phone' ? 'text-white' : 'text-text-dark-medium'
                }`}
                onClick={() => {
                  void changeMode('phone');
                }}
              >
                Phone Login
              </button>
            </div>
            <p className="mt-2 px-1 text-xs text-text-dark-medium">
              Swipe left or right to switch login mode. Phone login works for
              accounts with verified phone numbers.
            </p>
          </div>

          <div className="space-y-2">
            {loginMode === 'email' ? (
              <Controller
                name="email"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <IonicInput
                    {...field}
                    size="xlg"
                    type="email"
                    label="Email"
                    aria-label="Email"
                    placeholder="Enter your email"
                    {...register('email')}
                    hint={errors.email?.message}
                    hintClass="text-red-600 dark:text-red-600"
                  />
                )}
              />
            ) : (
              <Controller
                name="phone"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <IonicInput
                    {...field}
                    size="xlg"
                    type="tel"
                    label="Phone"
                    aria-label="Phone"
                    placeholder="Enter your phone number"
                    {...register('phone')}
                    hint={errors.phone?.message}
                    hintClass="text-red-600 dark:text-red-600"
                  />
                )}
              />
            )}

            {/* Password Input */}
            <Controller
              name="password"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <PasswordInput
                  // ref={inputRefs.password}
                  innerContainerClass="pr-0"
                  size="xlg"
                  label="Password"
                  aria-label="Password"
                  placeholder="********"
                  {...field}
                  hint={errors.password?.message}
                  hintClass="text-red-600 dark:text-red-600"
                />
              )}
            />

            {/** Remember... & Forgot password */}
            <div className="py-2">
              <Link
                to="/auth/forgot-password"
                className="block ml-auto w-fit text-primary font-medium font-dm_sans"
              >
                Forgot password?
              </Link>
            </div>

            {/* Login Button */}
            <IonicButton type="submit" size="xlg" className="w-full">
              Login
            </IonicButton>
          </div>
          <p className="text-center medium-emphasis-text ">
            <span>Don't have an account? </span>
            <Link
              to="/auth/sign-up?step=1"
              className=" text-primary font-semibold"
            >
              Sign up
            </Link>
          </p>
        </form>
      </Layout>
    </>
  );
};

export default SignIn;
